'use client';

import { FREE_APPLY_USES_PER_DAY_ANON, FREE_APPLY_USES_PER_DAY_REGISTERED } from '@/lib/autotuneQuota';

import { CONTENT_FACTS } from '@/lib/content-facts';
import homeControls from '@/data/editorial/home-controls.json';
import landingControls from '@/data/editorial/landing-controls.json';
import React, { useRef, useCallback, useMemo, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { usePendingFileStore } from '../stores/pendingFileStore';
import { useLanguage, LANGS, LANG_LABELS } from '../hooks/useLanguage';
import { stripLangPrefix } from '../contexts/LanguageContext';
import type { Lang } from '../hooks/useLanguage';
import { trackArticleToolTransition, trackEvent, trackFileUpload, trackProcessFailed, trackToolStart } from '../lib/analytics';
import './HomePage.css';
const SheetMusicOCRPanel = React.lazy(() => import('../components/SheetMusicOCR/SheetMusicOCRPanel'));
import { HeaderAuthButton } from '../components/Auth/HeaderAuthButton';
import HomeLanding from './HomeLanding';
import { useAuthStore } from '../stores/authStore';

// In the Electron SPA build, 'next/link' and 'next/navigation' are aliased to
// react-router shims (see la-studio-desktop/vite.config.ts). This file must
// only import next/* navigation so the Next.js web build keeps working.
function isElectronApp(): boolean {
  return typeof window !== 'undefined' && Boolean((window as any).electronAPI);
}

// ===== Line-style SVG icons (Untitled UI inspired, 2px stroke) =====
const Icon: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="1em"
    height="1em"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const IconMicHandheld = (
  <Icon>
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M5 10v2a7 7 0 0 0 14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="22" />
  </Icon>
);

const IconLayers = (
  <Icon>
    <path d="M12 2 2 7l10 5 10-5-10-5z" />
    <path d="M2 17l10 5 10-5" />
    <path d="M2 12l10 5 10-5" />
  </Icon>
);

const IconSpeaker = (
  <Icon>
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
  </Icon>
);

const IconSpeakerMute = (
  <Icon>
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <line x1="23" y1="9" x2="17" y2="15" />
    <line x1="17" y1="9" x2="23" y2="15" />
  </Icon>
);

const IconSliders = (
  <Icon>
    <line x1="4" y1="21" x2="4" y2="14" />
    <line x1="4" y1="10" x2="4" y2="3" />
    <line x1="12" y1="21" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12" y2="3" />
    <line x1="20" y1="21" x2="20" y2="16" />
    <line x1="20" y1="12" x2="20" y2="3" />
    <line x1="1" y1="14" x2="7" y2="14" />
    <line x1="9" y1="8" x2="15" y2="8" />
    <line x1="17" y1="16" x2="23" y2="16" />
  </Icon>
);

const IconActivity = (
  <Icon>
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </Icon>
);

const IconWave = (
  <Icon>
    <path d="M2 12c1.5-4 3-4 4.5 0s3 4 4.5 0 3-4 4.5 0 3 4 4.5 0" />
  </Icon>
);

const IconAmp = (
  <Icon>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <circle cx="12" cy="13" r="3.5" />
    <line x1="6" y1="7.5" x2="9" y2="7.5" />
    <line x1="11" y1="7.5" x2="14" y2="7.5" />
    <circle cx="17.5" cy="7.5" r="0.6" fill="currentColor" stroke="none" />
  </Icon>
);

const IconMusicNote = (
  <Icon>
    <path d="M9 18V5l12-2v13" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="16" r="3" />
  </Icon>
);

const IconSparkles = (
  <Icon>
    <path d="M12 3l1.6 4.6L18 9l-4.4 1.4L12 15l-1.6-4.6L6 9l4.4-1.4z" />
    <path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z" />
    <path d="M5 14l.6 1.6L7 16l-1.4.4L5 18l-.6-1.6L3 16l1.4-.4z" />
  </Icon>
);

const IconHeadphones = (
  <Icon>
    <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
    <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z" />
    <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
  </Icon>
);

const IconMicStudio = (
  <Icon>
    <rect x="9" y="2" width="6" height="13" rx="3" />
    <line x1="9" y1="6" x2="15" y2="6" />
    <line x1="9" y1="10" x2="15" y2="10" />
    <path d="M5 11a7 7 0 0 0 14 0" />
    <line x1="12" y1="18" x2="12" y2="22" />
    <line x1="8" y1="22" x2="16" y2="22" />
  </Icon>
);

const IconDisc = (
  <Icon>
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="3" />
  </Icon>
);

const IconVideo = (
  <Icon>
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </Icon>
);

const IconCap = (
  <Icon>
    <path d="M22 10v6" />
    <path d="M2 10l10-5 10 5-10 5z" />
    <path d="M6 12v5c0 2 3 3 6 3s6-1 6-3v-5" />
  </Icon>
);

const IconHome = (
  <Icon>
    <path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-7h-6v7H4a1 1 0 0 1-1-1z" />
  </Icon>
);

const IconRepeat = (
  <Icon>
    <path d="M17 1l4 4-4 4" />
    <path d="M3 11V9a4 4 0 0 1 4-4h14" />
    <path d="M7 23l-4-4 4-4" />
    <path d="M21 13v2a4 4 0 0 1-4 4H3" />
  </Icon>
);

// File extension fallback for the upload MIME check — mobile/cloud pickers
// often hand back an empty type or 'application/octet-stream' for valid
// audio (and occasionally video-container) files.
import { ACCEPTED_AUDIO_EXTENSIONS, AUDIO_FILE_ACCEPT } from '../utils/audioFileTypes';

const TEXTS: Record<Lang, {
  title1: string;
  title2: string;
  subtitle: string;
  free: string;
  unlimited: string;
  noSignup: string;
  uploadText: string;
  uploadHint: string;
  tools: string;
  footer: string;
  editor: string;
  vocalName: string;
  vocalDesc: string;
  stemName: string;
  stemDesc: string;
  masterName: string;
  masterDesc: string;
  masterSoonText: string;
  masterSoonHint: string;
  noiseName: string;
  noiseDesc: string;
  dawName: string;
  dawDesc: string;
  bpmName: string;
  bpmDesc: string;
  autotuneName: string;
  autotuneDesc: string;
  namName: string;
  namDesc: string;
  audioMidiName: string;
  audioMidiDesc: string;
  sheetOcrName: string;
  sheetOcrDesc: string;
  musicGenName: string;
  musicGenDesc: string;
  loopForgeName: string;
  loopForgeDesc: string;
  heroTitle1: string;
  heroTitle2: string;
  heroDesc: string;
  ctaOpenEditor: string;
  ctaLearnMore: string;
  featuresTitle: string;
  feat1Title: string;
  feat1Desc: string;
  feat2Title: string;
  feat2Desc: string;
  feat3Title: string;
  feat3Desc: string;
  feat4Title: string;
  feat4Desc: string;
  feat5Title: string;
  feat5Desc: string;
  feat6Title: string;
  feat6Desc: string;
  techTitle: string;
  privacyTitle: string;
  privacyDesc: string;
  badgeNoDataSent: string;
  badgeInstallFree: string;
  whyFreeTitle: string;
  whyFreeDesc: string;
  compareTitle: string;
  compareThemTitle: string;
  compareThem: string[];
  compareUsTitle: string;
  compareUs: string[];
  seoContent: Record<string, string>;
  faqContent: Record<string, {q: string, a: string}[]>;
}> = {
  ja: {
    title1: 'AIでボーカルを抽出。',
    title2: 'ブラウザだけで完結。',
    subtitle: 'ボーカル除去、6ステム分離、マスタリング。\nインストール不要、ブラウザだけで完結。',
    free: '基本無料',
    unlimited: '主要機能無料',
    noSignup: '登録不要',
    uploadText: '音声ファイルをドロップ、またはクリックして選択',
    uploadHint: 'MP3, WAV, FLAC, M4A 対応',
    tools: 'ツール',
    footer: 'LA STUDIO — ブラウザから生まれるサウンド',
    editor: 'エディタ',
    vocalName: 'ボーカル除去',
    vocalDesc: 'カラオケ・アカペラを即作成。アップロード不要。',
    stemName: 'ステム分離',
    stemDesc: 'ドラム・ベース・ギターを6パートに分解。',
    masterName: 'AIマスタリング',
    masterDesc: 'ラウドネス最適化、EQ、コンプレッサーで音圧を整える',
    masterSoonText: 'エディタで AI マスタリングを開く',
    masterSoonHint: 'ミックスを読み込んで、DEVICES ラックの AI MASTER で仕上げます。',
    noiseName: 'ノイズ除去',
    noiseDesc: '収録音声のノイズ・ハム音をAIで一発除去。',
    dawName: 'DAWエディタ',
    dawDesc: 'マルチトラック編集までブラウザで完結。',
    bpmName: 'BPM検出',
    bpmDesc: 'テンポとキーを自動解析。',
    autotuneName: 'オートチューン',
    autotuneDesc: 'ボーカルのピッチをAIが自然に補正。',
    namName: 'ギターアンプ (NAM)',
    namDesc: 'ニューラルアンプモデリングで本物のアンプサウンドをブラウザで。',
    audioMidiName: '音声→MIDI変換',
    audioMidiDesc: 'AIで楽曲をMIDIノートに変換。ピアノ・ギター・ボーカルに対応。',
    sheetOcrName: '楽譜OCR',
    sheetOcrDesc: '楽譜画像をAIが読み取り、デジタル楽譜に変換。',
    musicGenName: 'AI音楽生成',
    musicGenDesc: 'プロンプトや既存音源からAIが楽曲を生成。テキスト→楽曲・カバー・リペイント・ボーカル→伴奏の7モード対応。',
    loopForgeName: 'AIループ生成',
    loopForgeDesc: 'プロンプトから11秒のループを生成。128BPMなどテンポ指定も可能、無料2回/日。',
    heroTitle1: 'プロのスタジオが、',
    heroTitle2: 'ブラウザの中に。',
    heroDesc: '録音、ミキシング、マスタリングまで — すべてブラウザで完結。\nインストール不要。基本無料・一部Pro機能あり。',
    ctaOpenEditor: 'スタジオに入る — 無料・登録不要',
    ctaLearnMore: '機能を見る',
    featuresTitle: '主な機能',
    feat1Title: 'AIステム分離',
    feat1Desc: '楽曲をボーカル、ドラム、ベース、ギター、ピアノ、その他の6パートに分離。カラオケやリミックスに最適。',
    feat2Title: 'AIノイズ除去',
    feat2Desc: 'DeepFilterNet3搭載。録音のノイズ、ハム、環境音をワンクリックで除去。',
    feat3Title: 'マルチトラックエディタ',
    feat3Desc: 'プロ仕様のタイムライン編集。複数トラックの配置、カット、フェードをブラウザ上で。',
    feat4Title: 'ミキサー&エフェクト',
    feat4Desc: 'トラックごとのボリューム、パン、EQ、コンプレッサー、リバーブ。本格的なミキシング。',
    feat5Title: '100%ブラウザ',
    feat5Desc: 'インストール不要。すべてのAI処理がブラウザ内で完結。すぐに使えます。',
    feat6Title: 'WebGPU搭載',
    feat6Desc: 'GPU高速化AI処理。大容量ファイルも高速に処理。最新のWeb技術をフル活用。',
    techTitle: 'テクノロジー',
    privacyTitle: 'あなたの音声はプライベート',
    privacyDesc: 'すべての処理はブラウザ内で完結。音声ファイルがサーバーにアップロードされることはありません。あなたのデータは常にあなたの端末に。',
    badgeNoDataSent: 'サーバー不要',
    badgeInstallFree: 'インストール不要',
    whyFreeTitle: 'なぜ基本無料なの？',
    whyFreeDesc: 'LA Studioの大半のAI処理はブラウザ（GPU）上で実行され、サーバーコストがかかりません。だから基本無料。無制限利用やHQクラウド処理、楽譜OCRなどサーバーコストがかかる機能は、少額のPro (¥500/月) または24時間パスでご利用いただけます。',
    compareTitle: '他のツールとの違い',
    compareThemTitle: '他のツール',
    compareThem: ['サーバーで処理するため待ち時間がある', '無料枠は回数・時間制限あり', '登録・ログインが必要', '処理のたびにアップロードが必要'],
    compareUsTitle: 'LA Studio',
    compareUs: ['ブラウザ内で即座に処理', '主要機能は無料で開始', '登録・ログイン不要', 'WebGPUで高速AI処理'],
    seoContent: {
      'karaoke-maker': 'LA Studioのカラオケメーカーは、好きな曲からボーカルを取り除き、歌ってみたや練習に使えるインスト音源を作れる無料ツールです。音声ファイルを選ぶだけでAIがボーカルと伴奏を分離し、インストWAVを試聴・ダウンロードできます。\n\n処理後はそのままブラウザDAWでボーカル録音トラックを作成できるため、カラオケ音源を作って終わりではなく、自分の歌を重ねて編集・ミックスするところまで進めます。登録不要、インストール不要で、処理はブラウザ内で完結します。',
      'vocal-remover': 'LA Studioのボーカルリムーバーは、AIを使って楽曲からボーカルとインストゥルメンタルを自動分離します。カラオケ音源の作成、アカペラの抽出、リミックス制作に最適です。無料・登録不要でご利用いただけます。すべての処理はお使いのブラウザ内で完結し、音声ファイルがサーバーにアップロードされることはありません。\n\nMP3、WAV、FLAC、M4Aなどの主要なオーディオ形式に対応。WebGPUを活用した高速AI処理により、数分の楽曲でも素早く分離が完了します。プロの音楽制作からカラオケの楽しみまで、幅広い用途でお使いいただけます。',
      'stem-separator': 'LA Studioの6ステム分離は、1曲をボーカル、ドラム、ベース、ギター、ピアノ、その他の6パートに自動分離するAIツールです。リミックス制作、楽器練習、音楽学習に最適。無料・登録不要です。\n\nすべての処理はブラウザ内で完結し、音声データがサーバーに送信されることはありません。WebGPUによるGPU高速化で、大容量ファイルもスムーズに処理。分離された各パートは個別にダウンロード可能です。',
      'noise-removal': 'LA StudioのAIノイズ除去は、DeepFilterNet3を搭載した高精度ノイズリダクションツールです。録音に含まれるホワイトノイズ、ハム音、環境音、風切り音などを自動的に除去します。ポッドキャスト、インタビュー、ボイスメモのクリーンアップに最適。\n\n無料・登録不要。すべての処理はブラウザ内で完結し、音声ファイルがサーバーにアップロードされることはありません。ワンクリックでプロ品質のノイズ除去が可能です。',
      'editor': 'LA StudioのDAWエディタは、ブラウザで動作するプロ仕様のマルチトラックオーディオエディタです。複数トラックの配置、カット、フェード、ミキシングが可能。EQ、コンプレッサー、リバーブなどのエフェクトも搭載。\n\nインストール不要、無料で利用可能（96kHz書き出しなど一部はPro）。WebGPU対応の高速処理エンジンを搭載し、大規模プロジェクトにも対応。音楽制作、ポッドキャスト編集、サウンドデザインなど幅広い用途でお使いいただけます。',
      'bpm-detect': 'LA StudioのBPM検出ツールは、曲のテンポとキーをブラウザ上で自動解析します。DJのセット準備、リミックス制作、耳コピ、練習用トラックの整理に使いやすい無料ツールです。\n\n音声ファイルをアップロードすると、AIがBPMとキーを高速に推定します。インストール不要、登録不要で使え、解析はブラウザ内で完結します。音源をサーバーに送らずにテンポ解析したい場合に向いています。',
      'autotune': 'LA Studioのオートチューンは、Melodyne風の「ノート単位ピッチ編集」がブラウザで無料で使える唯一のツールです。AIが歌声のすべてのノートを検出してピアノロール上に表示し、ノートをドラッグするだけでセント単位の無段階ピッチ補正、範囲選択でのまとめて移動、ビブラートの増減、フォルマント（声質）調整、ピッチドリフト修正ができます。編集は約1秒でレンダリングされ自動で試聴されるため、書き出さずにその場で結果を確認できます。\n\nワンクリック補正なら、Lyra HQ エンジンが曲のキーとスケールを自動検出してボーカル全体を補正します（編集・試聴は無料。適用には1日の無料枠があり、Proで無制限）。さらに大きな音程変更にはニューラルボコーダ採用の AI Natural HQ エンジン（Pro）も選べます。MelodyneやAuto-Tuneの無料代替を探している方に: インストール不要・登録不要で、音声はブラウザの外に出ません。',
      'audio-to-midi': 'LA Studioの音声からMIDI変換ツールは、Spotify Basic Pitch を使ってメロディや和音をMIDIノートへ変換します。ピアノ、ギター、ボーカルなどの採譜補助に使え、DAWへ読み込む前処理にも便利です。\n\nポリフォニックな音源にも対応し、ブラウザだけで変換できます。登録不要、インストール不要で、音声ファイルをサーバーへ送らずにMIDI化できるため、素早くフレーズを確認したい用途に向いています。',
    },
    faqContent: {
      'karaoke-maker': [
        { q: 'カラオケ音源を無料で作れますか？', a: 'はい。音声ファイルをアップロードすると、AIがボーカルを分離してインスト音源を作成します。登録不要で使えます。' },
        { q: '作ったインスト音源はダウンロードできますか？', a: 'はい。処理後にインストWAVを試聴し、そのままダウンロードできます。' },
        { q: '自分の歌を重ねて録音できますか？', a: 'はい。ボーカル除去後に録音用トラックを作成し、ブラウザDAW上で歌を重ねる流れに進めます。' },
      ],
      'vocal-remover': [
        { q: 'ボーカル除去は無料ですか？', a: 'はい、登録不要で無料利用できます。ボーカル除去・ステム分離・ノイズ除去・DAW編集など、ブラウザ内で動く主要ツールは無料です。クラウド保存やサーバー推論が必要な高度機能はPro (¥500/月) で利用できます。' },
        { q: '対応フォーマットは？', a: 'MP3・WAV・FLAC・M4A等に対応しています。' },
        { q: 'カラオケ音源を作るのに使えますか？', a: 'はい、ボーカルを除去したインスト版を作成できます。' },
      ],
      // 'stem-separator' and 'autotune' FAQs are now server-rendered on their
      // tool pages via ToolLandingSection + TOOL_SEO.faq (richer content and
      // crawl-time FAQPage JSON-LD). Keeping entries here would render a
      // second FAQ section and inject a duplicate FAQPage schema post-
      // hydration, so they must stay removed.
      'noise-removal': [
        { q: 'どんなノイズに対応していますか？', a: '環境音、ハム音（電源ノイズ）、エアコン音、マイクノイズなどに対応しています。' },
        { q: '音質は劣化しますか？', a: 'AIによる高精度な処理のため、音質劣化を最小限に抑えたノイズ除去が可能です。' },
      ],
      'bpm-detect': [
        { q: 'BPMとキーを同時に検出できますか？', a: 'はい、テンポ（BPM）と楽曲のキー（調）を同時に解析します。' },
        { q: '精度はどのくらいですか？', a: 'AIによる高精度解析で、一般的な楽曲では±1BPM以内の精度です。' },
      ],
      'audio-to-midi': [
        { q: '音声をMIDIに変換できますか？', a: 'はい、Spotify Basic Pitch によるAI採譜で音声ファイルをMIDIノートへ変換できます。' },
        { q: '和音のある音源にも対応しますか？', a: 'はい、ポリフォニック対応なのでピアノやギターのフレーズ解析にも向いています。' },
      ],
    },
  },
  en: {
    title1: 'Extract Vocals with AI.',
    title2: 'Right in Your Browser.',
    subtitle: 'Vocal removal, 6-stem separation, mastering.\nNo install needed — works entirely in your browser.',
    free: 'Free to Start',
    unlimited: 'Core Tools Free',
    noSignup: 'No Sign-up',
    uploadText: 'Drop an audio file, or click to browse',
    uploadHint: 'MP3, WAV, FLAC, M4A supported',
    tools: 'Tools',
    footer: 'LA STUDIO — The Sound from Your Browser',
    editor: 'Editor',
    vocalName: 'Vocal Remover',
    vocalDesc: 'Separate vocals from instrumentals. Create karaoke & acapella.',
    stemName: 'Stem Separation',
    stemDesc: 'Split into 6 stems: drums, bass, vocals, guitar, piano & other',
    masterName: 'AI Mastering',
    masterDesc: 'Loudness optimization, EQ, compressor — studio-quality audio',
    masterSoonText: 'Open AI Mastering in the editor',
    masterSoonHint: 'Load your mix and finish it with AI MASTER in the DEVICES rack.',
    noiseName: 'Noise Removal',
    noiseDesc: 'Remove noise, hum, and background sounds with AI',
    dawName: 'DAW Editor',
    dawDesc: 'Full-featured audio editor with multi-track editing',
    bpmName: 'BPM Detector',
    bpmDesc: 'Auto-detect tempo and key of any song',
    autotuneName: 'Autotune',
    autotuneDesc: 'AI pitch correction for natural vocal tuning',
    namName: 'Guitar Amp (NAM)',
    namDesc: 'Neural amp modeling — real amp tones, right in your browser',
    audioMidiName: 'Audio to MIDI',
    audioMidiDesc: 'Convert any song to MIDI notes with AI. Polyphonic — piano, guitar, vocals.',
    sheetOcrName: 'Sheet Music OCR',
    sheetOcrDesc: 'AI reads sheet music images and converts them to digital notation.',
    musicGenName: 'AI Music Generator',
    musicGenDesc: 'Generate songs from a text prompt, restyle existing tracks, regenerate sections, or create backing music from a vocal. Seven modes powered by ACE-Step v1.5 XL.',
    loopForgeName: 'AI Loop Generator',
    loopForgeDesc: 'Generate an 11-second loop from a text prompt — set the BPM, genre, and instruments. 2 free per day.',
    heroTitle1: 'A Pro Studio,',
    heroTitle2: 'In Your Browser.',
    heroDesc: 'Recording, mixing, mastering — all in your browser.\nNo install. Core tools are free to use.',
    ctaOpenEditor: 'Enter the Studio — Free',
    ctaLearnMore: 'Explore Features',
    featuresTitle: 'Key Features',
    feat1Title: 'AI Stem Separation',
    feat1Desc: 'Split any song into 6 stems: vocals, drums, bass, guitar, piano & other. Perfect for karaoke and remixes.',
    feat2Title: 'AI Noise Removal',
    feat2Desc: 'Powered by DeepFilterNet3. Remove noise, hum, and background sounds from recordings with one click.',
    feat3Title: 'Multi-track Editor',
    feat3Desc: 'Professional timeline editing. Arrange, cut, and fade multiple tracks right in your browser.',
    feat4Title: 'Mixer & Effects',
    feat4Desc: 'Per-track volume, pan, EQ, compressor, and reverb. Full-featured mixing console.',
    feat5Title: '100% Browser',
    feat5Desc: 'No install needed. All AI processing runs entirely in your browser. Ready to use instantly.',
    feat6Title: 'WebGPU Powered',
    feat6Desc: 'GPU-accelerated AI inference. Process large files at blazing speed using cutting-edge web technology.',
    techTitle: 'Technology',
    privacyTitle: 'Your Audio Stays Private',
    privacyDesc: 'All processing happens entirely in your browser. No audio files are ever uploaded to a server. Your data always stays on your device.',
    badgeNoDataSent: 'No server needed',
    badgeInstallFree: 'Install-free',
    whyFreeTitle: 'Why can core tools stay free?',
    whyFreeDesc: 'Most LA Studio AI processing runs in your browser using your GPU. Server-powered features such as cloud saves, high-quality export, and OCR are available on Pro.',
    compareTitle: 'How we\'re different',
    compareThemTitle: 'Other tools',
    compareThem: ['Server processing with wait times', 'Free tier has usage limits', 'Signup / login required', 'Upload required for each file'],
    compareUsTitle: 'LA Studio',
    compareUs: ['Instant processing in your browser', 'Core tools free to use', 'No signup or login needed', 'Fast AI processing with WebGPU'],
    seoContent: {
      'karaoke-maker': "LA Studio's Karaoke Maker removes vocals from a song and creates an instrumental backing track you can sing over. Upload an audio file, let the AI separate vocals from the instrumental, then preview and download the instrumental WAV directly in your browser.\n\nAfter creating the karaoke track, you can continue into the browser DAW, prepare a vocal recording track, and record your own cover on top of the instrumental. No installation or account is required, and audio processing runs locally in the browser.",
      'vocal-remover': "LA Studio's Vocal Remover uses advanced AI to automatically separate vocals from instrumentals in any song. Perfect for creating karaoke tracks, extracting acapella, or preparing stems for remixes. Free to use, no sign-up required. All processing happens in your browser — your audio files are never uploaded to any server.\n\nSupports MP3, WAV, FLAC, M4A and other major audio formats. Powered by WebGPU for fast GPU-accelerated AI inference, even long tracks are processed quickly. Whether you're a music producer, DJ, or karaoke enthusiast, our vocal remover delivers professional-quality results.",
      'stem-separator': "LA Studio's 6-Stem Separation splits any song into vocals, drums, bass, guitar, piano, and other — six individual tracks using state-of-the-art AI. Ideal for remix production, instrument practice, music transcription, and sampling. Free to use with no registration.\n\nAll processing runs locally in your browser. No audio data is ever sent to a server, ensuring complete privacy. WebGPU acceleration enables fast processing of even large audio files. Each separated stem can be downloaded individually for use in your projects.",
      'noise-removal': "LA Studio's AI Noise Removal is powered by DeepFilterNet3, a state-of-the-art deep learning model for speech enhancement. Automatically removes white noise, hum, background chatter, wind noise, and other unwanted sounds from your recordings. Perfect for cleaning up podcasts, interviews, voice memos, and field recordings.\n\nFree to use, no sign-up required. All processing happens entirely in your browser — your audio files are never uploaded to any server. One-click professional-quality noise reduction with real-time preview.",
      'editor': "LA Studio's DAW Editor is a professional multi-track audio editor that runs entirely in your browser. Arrange, cut, fade, and mix multiple audio tracks with a full-featured timeline. Built-in effects include EQ, compressor, reverb, and more.\n\nNo installation required — free to use. Powered by WebGPU for fast, responsive editing even with large projects. Suitable for music production, podcast editing, sound design, and audio post-production. Export your finished project in WAV or MP3 format.",
      'bpm-detect': "LA Studio's BPM Detector analyzes the tempo and musical key of songs directly in your browser. It is useful for DJ prep, remix planning, practice sessions, and organizing reference tracks by BPM.\n\nUpload an audio file and the tool estimates BPM and key quickly with no sign-up required. Because processing runs locally in the browser, you can analyze tracks without sending audio to a remote server.",
      'autotune': "LA Studio's Autotune is the only free browser tool with Melodyne-style note-based pitch editing. AI analyzes your vocal and draws every sung note as an editable blob on a piano roll: drag a note up or down to retune it continuously at cent resolution, select a time range to move several notes together, flatten or deepen vibrato, shift formants to change vocal character, and fix pitch drift — each edit renders in about a second and plays back automatically, so you hear exactly what you changed without exporting.\n\nFor one-click correction, the Lyra HQ engine detects your song's key and scale and tunes the whole vocal automatically — editing and preview are free, and applying has a daily free limit (Pro makes it unlimited). An optional AI Natural HQ engine uses a neural vocoder (SiFiGAN) to render large pitch shifts with more natural voice quality. If you've been looking for a free alternative to Melodyne or Auto-Tune that runs without installing anything, this is it: no account needed, and your audio never leaves your browser.",
      'audio-to-midi': "LA Studio's Audio to MIDI converter uses Spotify Basic Pitch to turn audio into MIDI notes in your browser. It helps with transcription, melody extraction, chord sketching, and getting piano, guitar, or vocal phrases into your DAW faster.\n\nThe tool supports polyphonic transcription, so it can work with more than simple monophonic lines. No sign-up or install is required, and your audio stays local while the MIDI conversion runs in the browser.",
    },
    faqContent: {
      'karaoke-maker': [
        { q: 'Can I make karaoke tracks for free?', a: 'Yes. Upload a song and the AI will separate vocals from the instrumental so you can create a karaoke backing track.' },
        { q: 'Can I download the instrumental?', a: 'Yes. After processing, you can preview and download the instrumental WAV.' },
        { q: 'Can I record my own vocals over it?', a: 'Yes. After vocal removal, LA Studio can create an armed vocal recording track in the browser DAW.' },
      ],
      'vocal-remover': [
        { q: 'Is the vocal remover free?', a: 'Yes, you can use vocal removal for free with no signup.' },
        { q: 'What formats are supported?', a: 'MP3, WAV, FLAC, M4A and more.' },
        { q: 'Can I make karaoke tracks?', a: 'Yes, you can create instrumental versions by removing vocals.' },
      ],
      // 'stem-separator' and 'autotune' FAQs moved to the server-rendered
      // ToolLandingSection (TOOL_SEO.faq) — see the note in the JA block.
      'noise-removal': [
        { q: 'What types of noise can it remove?', a: 'Background noise, hum, AC noise, mic noise and more.' },
        { q: 'Does it degrade audio quality?', a: 'AI processing minimizes quality loss while effectively removing noise.' },
      ],
      'bpm-detect': [
        { q: 'Can it detect both BPM and key?', a: 'Yes, it analyzes both tempo (BPM) and musical key simultaneously.' },
        { q: 'How accurate is it?', a: 'AI-powered analysis achieves ±1 BPM accuracy for most songs.' },
      ],
      'audio-to-midi': [
        { q: 'Can it convert audio files to MIDI?', a: 'Yes, it uses Spotify Basic Pitch to transcribe audio into MIDI notes in the browser.' },
        { q: 'Does it support chords and polyphonic audio?', a: 'Yes, polyphonic transcription is supported, which makes it useful for piano and guitar phrases.' },
      ],
    },
  },
  ko: {
    title1: 'AI로 보컬을 추출.',
    title2: '무료로 시작, 브라우저에서 바로.',
    subtitle: '보컬 제거, 6스템 분리, 마스터링.\n설치 불필요 — 브라우저만으로 완결.',
    free: '무료로 시작',
    unlimited: '핵심 기능 무료',
    noSignup: '가입 불필요',
    uploadText: '오디오 파일을 드롭하거나 클릭하여 선택',
    uploadHint: 'MP3, WAV, FLAC, M4A 지원',
    tools: '도구',
    footer: 'LA STUDIO — 브라우저에서 태어나는 사운드',
    editor: '에디터',
    vocalName: '보컬 리무버',
    vocalDesc: '보컬과 반주를 분리. 노래방/아카펠라 제작에',
    stemName: '스템 분리',
    stemDesc: '드럼, 베이스, 보컬, 기타, 피아노, 기타 6파트로 분리',
    masterName: 'AI 마스터링',
    masterDesc: '라우드니스 최적화, EQ, 컴프레서로 음압 조정',
    masterSoonText: '에디터에서 AI 마스터링 열기',
    masterSoonHint: '믹스를 불러와 DEVICES 랙의 AI MASTER로 마무리하세요.',
    noiseName: '노이즈 제거',
    noiseDesc: '노이즈, 험, 환경음을 AI로 제거',
    dawName: 'DAW 에디터',
    dawDesc: '풀 기능 오디오 에디터. 멀티트랙 편집',
    bpmName: 'BPM 감지',
    bpmDesc: '곡의 템포와 키를 자동 감지',
    autotuneName: '오토튠',
    autotuneDesc: 'AI 피치 보정으로 자연스러운 보컬 튜닝',
    namName: '기타 앰프 (NAM)',
    namDesc: '뉴럴 앰프 모델링으로 진짜 앰프 사운드를 브라우저에서',
    audioMidiName: '오디오 to MIDI',
    audioMidiDesc: 'AI로 곡을 MIDI 노트로 변환. 피아노, 기타, 보컬 지원.',
    sheetOcrName: '악보 OCR',
    sheetOcrDesc: '악보 이미지를 AI로 읽어 디지털 음표로 변환합니다.',
    musicGenName: 'AI 음악 생성',
    musicGenDesc: '텍스트나 오디오를 바탕으로 새 음악을 생성합니다.',
    loopForgeName: 'AI 루프 생성기',
    loopForgeDesc: '텍스트 프롬프트로 11초 루프를 생성합니다 — BPM, 장르, 악기 지정 가능. 하루 2회 무료.',
    heroTitle1: '프로 스튜디오가,',
    heroTitle2: '브라우저 안에.',
    heroDesc: '녹음, 믹싱, 마스터링 — 모두 브라우저에서 완결.\n설치 불필요. 핵심 도구는 무료 — 무제한 이용과 HQ 클라우드 처리는 Pro($5/월).',
    ctaOpenEditor: '스튜디오에 입장 — 무료',
    ctaLearnMore: '기능 살펴보기',
    featuresTitle: '주요 기능',
    feat1Title: 'AI 스템 분리',
    feat1Desc: '노래를 보컬, 드럼, 베이스, 기타, 피아노, 기타 6파트로 분리. 노래방이나 리믹스에 최적.',
    feat2Title: 'AI 노이즈 제거',
    feat2Desc: 'DeepFilterNet3 탑재. 녹음의 노이즈, 험, 환경음을 원클릭으로 제거.',
    feat3Title: '멀티트랙 에디터',
    feat3Desc: '프로 수준의 타임라인 편집. 여러 트랙을 브라우저에서 배치, 컷, 페이드.',
    feat4Title: '믹서 & 이펙트',
    feat4Desc: '트랙별 볼륨, 팬, EQ, 컴프레서, 리버브. 본격적인 믹싱 콘솔.',
    feat5Title: '100% 브라우저',
    feat5Desc: '서버 업로드 불필요. 모든 처리가 로컬에서 완료. 데이터가 기기를 떠나지 않습니다.',
    feat6Title: 'WebGPU 탑재',
    feat6Desc: 'GPU 가속 AI 처리. 대용량 파일도 빠르게 처리. 최신 웹 기술을 활용.',
    techTitle: '기술',
    privacyTitle: '오디오는 프라이빗하게',
    privacyDesc: '모든 처리는 브라우저 내에서 완료됩니다. 오디오 파일이 서버에 업로드되지 않습니다. 데이터는 항상 기기에 남아있습니다.',
    badgeNoDataSent: 'No server needed',
    badgeInstallFree: 'Install-free',
    whyFreeTitle: 'Why can core tools stay free?',
    whyFreeDesc: 'Most LA Studio AI processing runs in your browser using your GPU. Server-powered features such as cloud saves, high-quality export, and unlimited use are available on Pro.',
    compareTitle: 'How we\'re different',
    compareThemTitle: 'Other tools',
    compareThem: ['Server processing with wait times', 'Free tier has usage limits', 'Signup / login required', 'Upload required for each file'],
    compareUsTitle: 'LA Studio',
    compareUs: ['Instant processing in your browser', 'Core tools free to use', 'No signup or login needed', 'Fast AI processing with WebGPU'],
    seoContent: {},
    faqContent: {},
  },
  zh: {
    title1: 'AI提取人声。',
    title2: '免费开始，浏览器即可使用。',
    subtitle: '人声去除、6轨分离、母带处理。\n无需安装 — 浏览器内完成一切。',
    free: '免费开始',
    unlimited: '核心功能免费',
    noSignup: '无需注册',
    uploadText: '拖放音频文件，或点击选择',
    uploadHint: '支持 MP3, WAV, FLAC, M4A',
    tools: '工具',
    footer: 'LA STUDIO — 源自浏览器的声音',
    editor: '编辑器',
    vocalName: '人声去除',
    vocalDesc: '分离人声与伴奏。制作卡拉OK/清唱版',
    stemName: '音轨分离',
    stemDesc: '分离为6轨：鼓、贝斯、人声、吉他、钢琴、其他',
    masterName: 'AI母带',
    masterDesc: '响度优化、EQ、压缩器 — 专业音质',
    masterSoonText: '在编辑器中打开 AI 母带处理',
    masterSoonHint: '载入你的混音，用 DEVICES 机架里的 AI MASTER 完成最后一步。',
    noiseName: '降噪',
    noiseDesc: '用AI去除噪音、嗡嗡声和环境音',
    dawName: 'DAW编辑器',
    dawDesc: '全功能音频编辑器，支持多轨编辑',
    bpmName: 'BPM检测',
    bpmDesc: '自动检测歌曲的速度和调性',
    autotuneName: '自动调音',
    autotuneDesc: 'AI音高校正，自然修正人声音准',
    namName: '吉他音箱 (NAM)',
    namDesc: '神经网络音箱建模 — 在浏览器中获得真实的音箱音色',
    audioMidiName: '音频转 MIDI',
    audioMidiDesc: '用 AI 将歌曲转换为 MIDI 音符。支持钢琴、吉他、人声等复音乐器。',
    sheetOcrName: '乐谱 OCR',
    sheetOcrDesc: '用 AI 识别乐谱图片并转换为数字音符。',
    musicGenName: 'AI 音乐生成',
    musicGenDesc: '根据文本或音频提示生成新的音乐片段。',
    loopForgeName: 'AI循环生成器',
    loopForgeDesc: '根据文字提示生成11秒循环片段——可指定BPM、风格与乐器。每天2次免费。',
    heroTitle1: '专业录音棚，',
    heroTitle2: '就在浏览器中。',
    heroDesc: '录音、混音、母带 — 全部在浏览器中完成。\n无需安装。核心工具免费使用 — 无限次使用与 HQ 云端处理需 Pro（$5/月）。',
    ctaOpenEditor: '进入工作室 — 免费',
    ctaLearnMore: '探索功能',
    featuresTitle: '核心功能',
    feat1Title: 'AI音轨分离',
    feat1Desc: '将歌曲分离为人声、鼓、贝斯、吉他、钢琴和其他6个音轨。完美用于卡拉OK和混音。',
    feat2Title: 'AI降噪',
    feat2Desc: '搭载DeepFilterNet3。一键去除录音中的噪音、嗡嗡声和环境音。',
    feat3Title: '多轨编辑器',
    feat3Desc: '专业级时间线编辑。在浏览器中排列、剪切和淡入淡出多个轨道。',
    feat4Title: '混音器和效果',
    feat4Desc: '每轨音量、声像、EQ、压缩器和混响。全功能混音控制台。',
    feat5Title: '100%浏览器',
    feat5Desc: '无需服务器上传。所有处理在本地完成。数据永远不会离开你的设备。',
    feat6Title: 'WebGPU驱动',
    feat6Desc: 'GPU加速AI推理。利用尖端网络技术快速处理大文件。',
    techTitle: '技术',
    privacyTitle: '你的音频保持私密',
    privacyDesc: '所有处理完全在浏览器中进行。音频文件不会上传到服务器。你的数据始终留在你的设备上。',
    badgeNoDataSent: 'No server needed',
    badgeInstallFree: 'Install-free',
    whyFreeTitle: 'Why can core tools stay free?',
    whyFreeDesc: 'Most LA Studio AI processing runs in your browser using your GPU. Server-powered features such as cloud saves, high-quality export, and unlimited use are available on Pro.',
    compareTitle: 'How we\'re different',
    compareThemTitle: 'Other tools',
    compareThem: ['Server processing with wait times', 'Free tier has usage limits', 'Signup / login required', 'Upload required for each file'],
    compareUsTitle: 'LA Studio',
    compareUs: ['Instant processing in your browser', 'Core tools free to use', 'No signup or login needed', 'Fast AI processing with WebGPU'],
    seoContent: {
      'autotune': 'LA Studio 的 Autotune 是唯一能在浏览器里免费使用「Melodyne 风格音符编辑」的工具。AI 会分析你的人声，把每个音符以可编辑的形状显示在钢琴卷帘上：拖动音符即可以音分精度无级修音，框选时间范围可同时移动多个音符，还能增减颤音、调整共振峰（音色）、修正音高漂移——每次编辑约 1 秒即可渲染并自动试听，无需导出就能确认效果。\n\n想要一键修音时，Lyra HQ 引擎会自动检测歌曲的调式并校正整段人声，编辑与试听免费（应用/导出有每日免费额度，Pro 可无限次）。还可选用 AI Natural HQ 引擎（神经网络声码器 SiFiGAN），让大幅修音也保持自然音色。如果你在寻找免安装的 Melodyne / Auto-Tune 免费替代品，这就是答案：无需注册，音频不会离开你的浏览器。',
    },
    // The 'autotune' FAQ moved to the server-rendered ToolLandingSection
    // (TOOL_SEO.faq) to avoid a duplicate FAQPage schema — see the JA block.
    faqContent: {},
  },
  es: {
    title1: 'Extrae vocales con IA.',
    title2: 'Gratis para empezar, en tu navegador.',
    subtitle: 'Eliminación de vocales, separación en 6 pistas, masterización.\nSin instalación — funciona completamente en tu navegador.',
    free: 'Gratis para empezar',
    unlimited: 'Herramientas gratis',
    noSignup: 'Sin registro',
    uploadText: 'Arrastra un archivo de audio o haz clic para seleccionar',
    uploadHint: 'Compatible con MP3, WAV, FLAC, M4A',
    tools: 'Herramientas',
    footer: 'LA STUDIO — El Sonido desde Tu Navegador',
    editor: 'Editor',
    vocalName: 'Eliminador de Voz',
    vocalDesc: 'Separa vocales e instrumentales. Crea karaoke y acapella.',
    stemName: 'Separación de Pistas',
    stemDesc: 'Divide en 6 pistas: Batería, Bajo, Vocales, Guitarra, Piano, Otros',
    masterName: 'Masterización IA',
    masterDesc: 'Optimización de volumen, EQ, compresor — calidad de estudio',
    masterSoonText: 'Abre la masterización con IA en el editor',
    masterSoonHint: 'Carga tu mezcla y termínala con AI MASTER en el rack DEVICES.',
    noiseName: 'Eliminación de Ruido',
    noiseDesc: 'Elimina ruido, zumbido y sonidos de fondo con IA',
    dawName: 'Editor DAW',
    dawDesc: 'Editor de audio completo con edición multipista',
    bpmName: 'Detector BPM',
    bpmDesc: 'Detecta automáticamente el tempo y la tonalidad',
    autotuneName: 'Autotune',
    autotuneDesc: 'Corrección de tono con IA para voces naturales',
    namName: 'Amplificador (NAM)',
    namDesc: 'Modelado neural de amplificadores — sonidos reales en tu navegador',
    audioMidiName: 'Audio a MIDI',
    audioMidiDesc: 'Convierte canciones a notas MIDI con IA. Polifónico — piano, guitarra, voces.',
    sheetOcrName: 'OCR de Partituras',
    sheetOcrDesc: 'La IA lee imágenes de partituras y las convierte en notación digital.',
    musicGenName: 'Generador de música IA',
    musicGenDesc: 'Genera canciones desde un prompt, cambia el estilo de pistas existentes, regenera secciones o crea música de acompañamiento desde una voz. Siete modos con ACE-Step v1.5 XL.',
    loopForgeName: 'Generador de Loops IA',
    loopForgeDesc: 'Genera un loop de 11 segundos a partir de un prompt de texto — indica el BPM, el género y los instrumentos. 2 gratis al día.',
    heroTitle1: 'Un Estudio Pro,',
    heroTitle2: 'En Tu Navegador.',
    heroDesc: 'Grabación, mezcla, masterización — todo en tu navegador.\nSin instalar. Las herramientas principales son gratis; Pro ($5/mes) añade uso ilimitado y procesamiento HQ en la nube.',
    ctaOpenEditor: 'Entrar al Estudio — Gratis',
    ctaLearnMore: 'Explorar Funciones',
    featuresTitle: 'Funciones Principales',
    feat1Title: 'Separación AI de Stems',
    feat1Desc: 'Divide cualquier canción en 6 stems: voz, batería, bajo, guitarra, piano y otros. Ideal para karaoke y remixes.',
    feat2Title: 'Eliminación AI de Ruido',
    feat2Desc: 'Con DeepFilterNet3. Elimina ruido, zumbido y sonidos de fondo de grabaciones con un clic.',
    feat3Title: 'Editor Multipista',
    feat3Desc: 'Edición profesional de línea de tiempo. Organiza, corta y aplica fundidos a múltiples pistas en tu navegador.',
    feat4Title: 'Mezclador y Efectos',
    feat4Desc: 'Volumen, paneo, EQ, compresor y reverberación por pista. Consola de mezcla completa.',
    feat5Title: '100% Navegador',
    feat5Desc: 'Sin carga al servidor. Todo el procesamiento se realiza localmente. Tus datos nunca salen de tu dispositivo.',
    feat6Title: 'Potenciado por WebGPU',
    feat6Desc: 'Inferencia AI acelerada por GPU. Procesa archivos grandes a alta velocidad con tecnología web de vanguardia.',
    techTitle: 'Tecnología',
    privacyTitle: 'Tu Audio Es Privado',
    privacyDesc: 'Todo el procesamiento ocurre en tu navegador. Ningún archivo de audio se sube a un servidor. Tus datos siempre permanecen en tu dispositivo.',
    badgeNoDataSent: 'No server needed',
    badgeInstallFree: 'Install-free',
    whyFreeTitle: 'Why can core tools stay free?',
    whyFreeDesc: 'Most LA Studio AI processing runs in your browser using your GPU. Server-powered features such as cloud saves, high-quality export, and unlimited use are available on Pro.',
    compareTitle: 'How we\'re different',
    compareThemTitle: 'Other tools',
    compareThem: ['Server processing with wait times', 'Free tier has usage limits', 'Signup / login required', 'Upload required for each file'],
    compareUsTitle: 'LA Studio',
    compareUs: ['Instant processing in your browser', 'Core tools free to use', 'No signup or login needed', 'Fast AI processing with WebGPU'],
    seoContent: {},
    faqContent: {},
  },
  fr: {
    title1: 'Extraire les voix avec l\'IA.',
    title2: 'Gratuit pour commencer, dans votre navigateur.',
    subtitle: 'Suppression vocale, séparation 6 pistes, mastering.\nSans installation — fonctionne entièrement dans votre navigateur.',
    free: 'Gratuit pour commencer',
    unlimited: 'Outils gratuits',
    noSignup: 'Sans inscription',
    uploadText: 'Déposez un fichier audio ou cliquez pour sélectionner',
    uploadHint: 'MP3, WAV, FLAC, M4A pris en charge',
    tools: 'Outils',
    footer: 'LA STUDIO — Le Son né de Votre Navigateur',
    editor: 'Éditeur',
    vocalName: 'Suppression Vocale',
    vocalDesc: 'Séparez les voix des instruments. Créez karaoké et acapella.',
    stemName: 'Séparation de Stems',
    stemDesc: 'Divisez en 6 pistes : Batterie, Basse, Voix, Guitare, Piano, Autres',
    masterName: 'Mastering IA',
    masterDesc: 'Optimisation du volume, EQ, compresseur — qualité studio',
    masterSoonText: 'Ouvrir le mastering IA dans l’éditeur',
    masterSoonHint: 'Chargez votre mix et finalisez-le avec AI MASTER dans le rack DEVICES.',
    noiseName: 'Suppression du Bruit',
    noiseDesc: 'Supprimez le bruit, le bourdonnement et les sons ambiants avec l\'IA',
    dawName: 'Éditeur DAW',
    dawDesc: 'Éditeur audio complet avec édition multipiste',
    bpmName: 'Détecteur BPM',
    bpmDesc: 'Détectez automatiquement le tempo et la tonalité',
    autotuneName: 'Autotune',
    autotuneDesc: 'Correction de hauteur IA pour un accordage vocal naturel',
    namName: 'Ampli Guitare (NAM)',
    namDesc: 'Modélisation neuronale d\'ampli — vrais sons d\'ampli dans votre navigateur',
    audioMidiName: 'Audio vers MIDI',
    audioMidiDesc: 'Convertissez des chansons en notes MIDI avec l\'IA. Polyphonique — piano, guitare, voix.',
    sheetOcrName: 'OCR de Partitions',
    sheetOcrDesc: 'L\'IA lit les images de partitions et les convertit en notation numérique.',
    musicGenName: 'Générateur de musique IA',
    musicGenDesc: 'Générez des morceaux à partir d\'un prompt, transformez le style de pistes existantes, régénérez des sections ou créez un accompagnement depuis une voix. Sept modes avec ACE-Step v1.5 XL.',
    loopForgeName: 'Générateur de Boucles IA',
    loopForgeDesc: 'Générez une boucle de 11 secondes à partir d\'un prompt texte — précisez le BPM, le genre et les instruments. 2 gratuites par jour.',
    heroTitle1: 'Un Studio Pro,',
    heroTitle2: 'Dans Votre Navigateur.',
    heroDesc: 'Enregistrement, mixage, mastering — tout dans votre navigateur.\nSans installation. Les outils principaux sont gratuits ; Pro (5 $/mois) ajoute l’usage illimité et le traitement cloud HQ.',
    ctaOpenEditor: 'Entrer au Studio — Gratuit',
    ctaLearnMore: 'Découvrir les Fonctions',
    featuresTitle: 'Fonctionnalités Clés',
    feat1Title: 'Séparation AI de Stems',
    feat1Desc: 'Divisez n\'importe quelle chanson en 6 stems : voix, batterie, basse, guitare, piano et autres. Idéal pour le karaoké et les remixes.',
    feat2Title: 'Suppression AI du Bruit',
    feat2Desc: 'Avec DeepFilterNet3. Supprimez bruit, bourdonnement et sons ambiants d\'un simple clic.',
    feat3Title: 'Éditeur Multipiste',
    feat3Desc: 'Édition professionnelle de la timeline. Arrangez, coupez et appliquez des fondus à plusieurs pistes dans votre navigateur.',
    feat4Title: 'Mixeur et Effets',
    feat4Desc: 'Volume, panoramique, EQ, compresseur et réverbération par piste. Console de mixage complète.',
    feat5Title: '100% Navigateur',
    feat5Desc: 'Aucun envoi serveur. Tout le traitement se fait localement. Vos données ne quittent jamais votre appareil.',
    feat6Title: 'Propulsé par WebGPU',
    feat6Desc: 'Inférence IA accélérée par GPU. Traitez de gros fichiers à grande vitesse avec une technologie web de pointe.',
    techTitle: 'Technologie',
    privacyTitle: 'Votre Audio Reste Privé',
    privacyDesc: 'Tout le traitement se fait dans votre navigateur. Aucun fichier audio n\'est envoyé à un serveur. Vos données restent toujours sur votre appareil.',
    badgeNoDataSent: 'No server needed',
    badgeInstallFree: 'Install-free',
    whyFreeTitle: 'Why can core tools stay free?',
    whyFreeDesc: 'Most LA Studio AI processing runs in your browser using your GPU. Server-powered features such as cloud saves, high-quality export, and unlimited use are available on Pro.',
    compareTitle: 'How we\'re different',
    compareThemTitle: 'Other tools',
    compareThem: ['Server processing with wait times', 'Free tier has usage limits', 'Signup / login required', 'Upload required for each file'],
    compareUsTitle: 'LA Studio',
    compareUs: ['Instant processing in your browser', 'Core tools free to use', 'No signup or login needed', 'Fast AI processing with WebGPU'],
    seoContent: {},
    faqContent: {},
  },
  de: {
    title1: 'Gesang mit KI extrahieren.',
    title2: 'Kostenlos starten, direkt im Browser.',
    subtitle: 'Gesangsentfernung, 6-Stem-Trennung, Mastering.\nKeine Installation — funktioniert komplett im Browser.',
    free: 'Kostenlos starten',
    unlimited: 'Kern-Tools gratis',
    noSignup: 'Ohne Anmeldung',
    uploadText: 'Audiodatei hierher ziehen oder klicken zum Auswählen',
    uploadHint: 'MP3, WAV, FLAC, M4A unterstützt',
    tools: 'Werkzeuge',
    footer: 'LA STUDIO — Der Sound aus deinem Browser',
    editor: 'Editor',
    vocalName: 'Gesangsentfernung',
    vocalDesc: 'Trennen Sie Gesang von Instrumenten. Erstellen Sie Karaoke & Acapella.',
    stemName: 'Stem-Trennung',
    stemDesc: 'In 6 Spuren aufteilen: Drums, Bass, Gesang, Gitarre, Klavier, Sonstige',
    masterName: 'KI-Mastering',
    masterDesc: 'Lautheitsoptimierung, EQ, Kompressor — Studioqualität',
    masterSoonText: 'KI-Mastering im Editor öffnen',
    masterSoonHint: 'Lade deinen Mix und finalisiere ihn mit AI MASTER im DEVICES-Rack.',
    noiseName: 'Rauschentfernung',
    noiseDesc: 'Entfernen Sie Rauschen, Brummen und Hintergrundgeräusche mit KI',
    dawName: 'DAW-Editor',
    dawDesc: 'Voll ausgestatteter Audio-Editor mit Mehrspurbearbeitung',
    bpmName: 'BPM-Erkennung',
    bpmDesc: 'Tempo und Tonart automatisch erkennen',
    autotuneName: 'Autotune',
    autotuneDesc: 'KI-Tonhöhenkorrektur für natürliches Vocal-Tuning',
    namName: 'Gitarrenverstärker (NAM)',
    namDesc: 'Neural Amp Modeling — echte Verstärker-Sounds direkt im Browser',
    audioMidiName: 'Audio zu MIDI',
    audioMidiDesc: 'Wandeln Sie Songs mit KI in MIDI-Noten um. Polyphon — Klavier, Gitarre, Gesang.',
    sheetOcrName: 'Noten-OCR',
    sheetOcrDesc: 'KI liest Notenbilder und wandelt sie in digitale Notation um.',
    musicGenName: 'KI-Musikgenerator',
    musicGenDesc: 'Generiere Songs aus einem Text-Prompt, style existierende Tracks um, generiere Abschnitte neu oder erstelle Begleitmusik zu einer Gesangsspur. Sieben Modi mit ACE-Step v1.5 XL.',
    loopForgeName: 'KI-Loop-Generator',
    loopForgeDesc: 'Erzeuge eine 11-Sekunden-Loop aus einem Text-Prompt — BPM, Genre und Instrumente angeben. 2 kostenlos pro Tag.',
    heroTitle1: 'Ein Profi-Studio,',
    heroTitle2: 'In Deinem Browser.',
    heroDesc: 'Aufnahme, Mixing, Mastering — alles im Browser.\nKeine Installation. Die Kern-Tools sind kostenlos; Pro (5 $/Monat) bietet unbegrenzte Nutzung und HQ-Cloud-Verarbeitung.',
    ctaOpenEditor: 'Studio betreten — Kostenlos',
    ctaLearnMore: 'Funktionen entdecken',
    featuresTitle: 'Hauptfunktionen',
    feat1Title: 'KI-Stem-Trennung',
    feat1Desc: 'Teilen Sie jeden Song in 6 Stems: Gesang, Drums, Bass, Gitarre, Klavier und Sonstiges. Ideal für Karaoke und Remixes.',
    feat2Title: 'KI-Rauschentfernung',
    feat2Desc: 'Mit DeepFilterNet3. Entfernen Sie Rauschen, Brummen und Hintergrundgeräusche mit einem Klick.',
    feat3Title: 'Mehrspur-Editor',
    feat3Desc: 'Professionelle Timeline-Bearbeitung. Mehrere Spuren im Browser anordnen, schneiden und überblenden.',
    feat4Title: 'Mixer & Effekte',
    feat4Desc: 'Lautstärke, Panorama, EQ, Kompressor und Hall pro Spur. Vollwertige Mischkonsole.',
    feat5Title: '100% Browser',
    feat5Desc: 'Kein Server-Upload nötig. Alle Verarbeitung läuft lokal. Ihre Daten verlassen nie Ihr Gerät.',
    feat6Title: 'WebGPU-betrieben',
    feat6Desc: 'GPU-beschleunigte KI-Inferenz. Verarbeiten Sie große Dateien blitzschnell mit modernster Webtechnologie.',
    techTitle: 'Technologie',
    privacyTitle: 'Ihr Audio bleibt privat',
    privacyDesc: 'Die gesamte Verarbeitung erfolgt in Ihrem Browser. Keine Audiodateien werden auf einen Server hochgeladen. Ihre Daten bleiben immer auf Ihrem Gerät.',
    badgeNoDataSent: 'No server needed',
    badgeInstallFree: 'Install-free',
    whyFreeTitle: 'Why can core tools stay free?',
    whyFreeDesc: 'Most LA Studio AI processing runs in your browser using your GPU. Server-powered features such as cloud saves, high-quality export, and unlimited use are available on Pro.',
    compareTitle: 'How we\'re different',
    compareThemTitle: 'Other tools',
    compareThem: ['Server processing with wait times', 'Free tier has usage limits', 'Signup / login required', 'Upload required for each file'],
    compareUsTitle: 'LA Studio',
    compareUs: ['Instant processing in your browser', 'Core tools free to use', 'No signup or login needed', 'Fast AI processing with WebGPU'],
    seoContent: {},
    faqContent: {},
  },
  pt: {
    title1: 'Extraia vocais com IA.',
    title2: 'Grátis para começar, no seu navegador.',
    subtitle: 'Remoção de vocal, separação em 6 faixas, masterização.\nSem instalação — funciona inteiramente no navegador.',
    free: 'Grátis para começar',
    unlimited: 'Ferramentas grátis',
    noSignup: 'Sem cadastro',
    uploadText: 'Arraste um arquivo de áudio ou clique para selecionar',
    uploadHint: 'MP3, WAV, FLAC, M4A suportados',
    tools: 'Ferramentas',
    footer: 'LA STUDIO — O Som do Seu Navegador',
    editor: 'Editor',
    vocalName: 'Remoção de Vocal',
    vocalDesc: 'Separe vocais de instrumentais. Crie karaokê e acapella.',
    stemName: 'Separação de Stems',
    stemDesc: 'Divida em 6 faixas: Bateria, Baixo, Vocais, Guitarra, Piano, Outros',
    masterName: 'Masterização com IA',
    masterDesc: 'Otimização de loudness, EQ, compressor — qualidade de estúdio',
    masterSoonText: 'Abrir a masterização com IA no editor',
    masterSoonHint: 'Carregue sua mixagem e finalize com o AI MASTER no rack DEVICES.',
    noiseName: 'Remoção de Ruído',
    noiseDesc: 'Remova ruído, zumbido e sons de fundo com IA',
    dawName: 'Editor DAW',
    dawDesc: 'Editor de áudio completo com edição multipista',
    bpmName: 'Detector de BPM',
    bpmDesc: 'Detecte automaticamente o andamento e a tonalidade',
    autotuneName: 'Autotune',
    autotuneDesc: 'Correção de tom com IA para afinação vocal natural',
    namName: 'Amplificador (NAM)',
    namDesc: 'Modelagem neural de amplificadores — sons reais no navegador',
    audioMidiName: 'Áudio para MIDI',
    audioMidiDesc: 'Converta músicas em notas MIDI com IA. Polifônico — piano, guitarra, vocal.',
    sheetOcrName: 'OCR de Partituras',
    sheetOcrDesc: 'Leia imagens de partituras com IA e converta em notas digitais.',
    musicGenName: 'Gerador de música IA',
    musicGenDesc: 'Gere novas faixas a partir de texto ou áudio de referência.',
    loopForgeName: 'Gerador de Loops IA',
    loopForgeDesc: 'Gere um loop de 11 segundos a partir de um prompt de texto — defina o BPM, o gênero e os instrumentos. 2 grátis por dia.',
    heroTitle1: 'Um Estúdio Pro,',
    heroTitle2: 'No Seu Navegador.',
    heroDesc: 'Gravação, mixagem, masterização — tudo no navegador.\nSem instalação. As ferramentas principais são grátis; o Pro (US$ 5/mês) adiciona uso ilimitado e processamento HQ na nuvem.',
    ctaOpenEditor: 'Entrar no Estúdio — Grátis',
    ctaLearnMore: 'Explorar Recursos',
    featuresTitle: 'Recursos Principais',
    feat1Title: 'Separação AI de Stems',
    feat1Desc: 'Divida qualquer música em 6 stems: vocais, bateria, baixo, guitarra, piano e outros. Perfeito para karaokê e remixes.',
    feat2Title: 'Remoção AI de Ruído',
    feat2Desc: 'Com DeepFilterNet3. Remova ruído, zumbido e sons de fundo de gravações com um clique.',
    feat3Title: 'Editor Multipista',
    feat3Desc: 'Edição profissional de timeline. Organize, corte e aplique fades em múltiplas faixas no navegador.',
    feat4Title: 'Mixer e Efeitos',
    feat4Desc: 'Volume, pan, EQ, compressor e reverb por faixa. Console de mixagem completa.',
    feat5Title: '100% Navegador',
    feat5Desc: 'Sem upload para servidor. Todo o processamento é local. Seus dados nunca saem do seu dispositivo.',
    feat6Title: 'WebGPU Integrado',
    feat6Desc: 'Inferência AI acelerada por GPU. Processe grandes arquivos em alta velocidade com tecnologia web de ponta.',
    techTitle: 'Tecnologia',
    privacyTitle: 'Seu Áudio Permanece Privado',
    privacyDesc: 'Todo o processamento acontece no seu navegador. Nenhum arquivo de áudio é enviado a um servidor. Seus dados sempre permanecem no seu dispositivo.',
    badgeNoDataSent: 'No server needed',
    badgeInstallFree: 'Install-free',
    whyFreeTitle: 'Why can core tools stay free?',
    whyFreeDesc: 'Most LA Studio AI processing runs in your browser using your GPU. Server-powered features such as cloud saves, high-quality export, and unlimited use are available on Pro.',
    compareTitle: 'How we\'re different',
    compareThemTitle: 'Other tools',
    compareThem: ['Server processing with wait times', 'Free tier has usage limits', 'Signup / login required', 'Upload required for each file'],
    compareUsTitle: 'LA Studio',
    compareUs: ['Instant processing in your browser', 'Core tools free to use', 'No signup or login needed', 'Fast AI processing with WebGPU'],
    seoContent: {},
    faqContent: {},
  },
};

const HOME_CONTENT: Record<string, {
  hitsville: { title: string; items: { title: string; desc: string }[] };
  howItWorks: { title: string; steps: { num: string; title: string; desc: string }[] };
  useCases: { title: string; items: { icon: React.ReactNode; title: string; desc: string }[] };
  homeFaq: { title: string; items: { q: string; a: string }[] };
}> = {
  ja: {
    hitsville: {
      title: 'The Hitsville Way',
      items: [
        {
          title: '小さな家から、世界へ',
          desc: '1959年、Berry Gordyは800ドルを借りてデトロイトの2階建ての家を買い、「Hitsville U.S.A.」と名付けた。ガレージをスタジオに、キッチンをコントロールルームに改造し、そこから世界を変える音楽が生まれた。',
        },
        {
          title: 'すべてを、ひとつの場所で',
          desc: 'モータウンの強みは「一気通貫」。作曲、編曲、録音、ミキシング、品質管理まで、すべてがひとつ屋根の下で完結した。だからインディーでもメジャーに勝てるクオリティが出せた。',
        },
        {
          title: 'あなたのブラウザが、スタジオになる',
          desc: 'LA Studioも同じ思想。録音、ステム分離、ノイズ除去、ミキシング、マスタリングまで — インストールなし、サーバー不要。ブラウザを開いた瞬間、あなたの部屋がHitsvilleになる。',
        },
      ],
    },
    howItWorks: {
      title: '使い方はシンプル',
      steps: [
        { num: '01', title: 'ファイルをドロップ', desc: 'MP3、WAV、FLAC — ブラウザにドラッグするだけ' },
        { num: '02', title: '処理して結果を確認', desc: '標準はブラウザ内で処理。初回はモデルの読み込みが必要です' },
        { num: '03', title: '完成。すぐ使える', desc: 'ダウンロード、またはそのままエディタで編集' },
      ],
    },
    useCases: {
      title: 'こんな人のためのスタジオ',
      items: [
        { icon: IconHeadphones, title: 'ベッドルームプロデューサー', desc: 'カバー曲のステム抽出、リミックス制作、自宅録音のミキシングに' },
        { icon: IconMicStudio, title: 'ポッドキャスター', desc: '収録音声のノイズ除去、マルチトラック編集、音量の整理に' },
        { icon: IconDisc, title: 'DJ / リミキサー', desc: '楽曲のBPM・キー検出、アカペラ抽出、インスト作成に' },
        { icon: IconVideo, title: '動画クリエイター', desc: 'BGMからボーカルを外す、ナレーションのノイズ除去に' },
        { icon: IconCap, title: '音楽を学ぶ人', desc: '楽器パートの聴き取り練習、楽曲構造の分析に' },
      ],
    },
    homeFaq: {
      title: 'よくある質問',
      items: [
        { q: '本当に無料ですか？', a: CONTENT_FACTS.ja.pricing },
        { q: '音声ファイルはサーバーに送信されますか？', a: CONTENT_FACTS.ja.privacy },
        { q: '対応ブラウザは？', a: CONTENT_FACTS.ja.browser },
        { q: '対応フォーマットは？', a: 'MP3、WAV、FLAC、M4A、OGGなど主要な音声フォーマットに対応しています。' },
        { q: 'アカウント登録は必要ですか？', a: CONTENT_FACTS.ja.account },
      ],
    },
  },
  en: {
    hitsville: {
      title: 'The Hitsville Way',
      items: [
        {
          title: 'From a Small House to the World',
          desc: 'In 1959, Berry Gordy borrowed $800 and bought a two-story house in Detroit, naming it "Hitsville U.S.A." He turned the garage into a studio, the kitchen into a control room, and from there, music that changed the world was born.',
        },
        {
          title: 'Everything Under One Roof',
          desc: "Motown's strength was vertical integration. Songwriting, arranging, recording, mixing, quality control — all happened under one roof. That's how independent artists could rival major-label quality.",
        },
        {
          title: 'Your Browser Becomes Your Studio',
          desc: 'LA Studio follows the same philosophy. Recording, stem separation, noise removal, mixing, mastering — no install, no server. The moment you open your browser, your room becomes Hitsville.',
        },
      ],
    },
    howItWorks: {
      title: 'How It Works',
      steps: [
        { num: '01', title: 'Drop Your File', desc: 'MP3, WAV, FLAC — just drag into the browser' },
        { num: '02', title: 'Process and check the result', desc: 'Standard runs locally. First use requires model loading' },
        { num: '03', title: 'Done. Ready to Use', desc: 'Download, or edit right away in the editor' },
      ],
    },
    useCases: {
      title: 'A Studio for Everyone',
      items: [
        { icon: IconHeadphones, title: 'Bedroom Producers', desc: 'Extract stems for covers, create remixes, mix home recordings' },
        { icon: IconMicStudio, title: 'Podcasters', desc: 'Remove noise from recordings, multi-track editing, level adjustment' },
        { icon: IconDisc, title: 'DJs & Remixers', desc: 'Detect BPM & key, extract acapella, create instrumentals' },
        { icon: IconVideo, title: 'Video Creators', desc: 'Remove vocals from BGM, clean up narration audio' },
        { icon: IconCap, title: 'Music Learners', desc: 'Isolate instrument parts for practice, analyze song structure' },
      ],
    },
    homeFaq: {
      title: 'Frequently Asked Questions',
      items: [
        { q: 'Is it free to start?', a: CONTENT_FACTS.en.pricing },
        { q: 'Are my audio files sent to a server?', a: CONTENT_FACTS.en.privacy },
        { q: 'Which browsers are supported?', a: CONTENT_FACTS.en.browser },
        { q: 'What audio formats are supported?', a: 'MP3, WAV, FLAC, M4A, OGG and other major audio formats are supported.' },
        { q: 'Do I need to create an account?', a: CONTENT_FACTS.en.account },
      ],
    },
  },
};

/**
 * SEO h1 overrides per tool (JA/EN only; other locales keep the short tool
 * name, as do the sidebar labels). Each phrase is the query family the page
 * actually ranks for in GSC — stem: the verified zero-competition angle;
 * karaoke/vocal-remover (2026-09-02): the JA オフボーカル cluster (~1,850
 * impr/28d) that lands on blog posts instead of these pages; amp-sim: the
 * "neural amp modeler" queries the LP was built for; bpm: 曲 キー 調べる.
 */
const H1_OVERRIDES: Record<string, { ja?: string; en?: string }> = {
  'stem-separator': {
    en: 'Free 6-Stem Splitter (Guitar + Piano) — No Signup',
    ja: '無料6ステム分離（ギター・ピアノ対応）— 登録不要',
  },
  'karaoke-maker': {
    ja: 'オフボーカル音源（カラオケ）を無料で作る — ブラウザ完結・登録不要',
  },
  'vocal-remover': {
    ja: 'ボーカル除去 — オフボーカル・アカペラを無料で作成',
  },
  nam: {
    en: 'Neural Amp Modeler (NAM) Online — Free Browser Amp Sim',
    ja: 'Neural Amp Modeler (NAM) をブラウザで — 無料アンプシミュレーター',
  },
  autotune: {
    en: 'Free Online Autotune for Vocals',
    ja: '無料オンラインオートチューン — 歌声の音程補正',
  },
  'bpm-detect': {
    ja: '曲のキー・BPMを調べる — 無料・ブラウザ完結',
  },
  editor: {
    en: 'Free Online DAW — Multitrack Editor in Your Browser',
  },
};

const HomePage: React.FC<{ toolPath?: string }> = ({ toolPath }) => {
  const router = useRouter();
  const navigate = useCallback((path: string) => {
    // Client-side navigation preserves Zustand store (pendingFileStore)
    router.push(path);
  }, [router]);
  const { setFile } = usePendingFileStore();
  const [dragOver, setDragOver] = React.useState(false);
  const { lang, setLang, localizedPath } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Server HTML must not offer actions before their handlers are attached.
  const [inputReady, setInputReady] = React.useState(false);
  useEffect(() => { setInputReady(true); }, []);
  const localeControls = landingControls[lang as keyof typeof landingControls];
  const preparingInput = localeControls?.preparing || (lang === 'ja' ? '操作の準備中…' : lang === 'zh' ? '正在准备…' : 'Getting ready…');

  const facts = CONTENT_FACTS[lang];
  const publicCopy = homeControls[lang as keyof typeof homeControls];
  const t = { ...TEXTS[lang], privacyDesc: facts.privacy, badgeNoDataSent: facts.localBadge,
    loopForgeDesc: publicCopy?.loopDesc || (lang === 'ja' ? '短いループをクラウドで生成。無料枠は未登録1回・登録済み5回/日（UTC）。' : 'Generate short loops in the cloud. Free quota: 1/day signed out, 5/day signed in (UTC).'), badgeInstallFree: publicCopy?.install || TEXTS[lang].badgeInstallFree, whyFreeTitle: publicCopy?.whyFree || TEXTS[lang].whyFreeTitle, vocalDesc: lang === 'ja' ? '標準のブラウザ内分離でボーカルと伴奏を作成。' : lang === 'en' ? 'Separate vocals and accompaniment with Standard browser processing.' : TEXTS[lang].vocalDesc, whyFreeDesc: facts.pricing, feat5Title: facts.localBadge, feat5Desc: facts.privacy, seoContent: {} as Record<string, string>, faqContent: {} as Record<string, {q:string;a:string}[]> };
  const home = HOME_CONTENT[lang] || {
    ...HOME_CONTENT.en,
    howItWorks: { title: publicCopy.howTitle, steps: publicCopy.steps.map((title, i) => ({num: `0${i+1}`, title, desc: [t.uploadHint, facts.localHow, facts.download][i]})) },
    useCases: { title: publicCopy.audienceTitle, items: publicCopy.audiences.map((title, i) => ({icon: HOME_CONTENT.en.useCases.items[i].icon, title, desc: [t.stemDesc, t.noiseDesc, t.bpmDesc, t.vocalDesc, t.audioMidiDesc][i]})) },
    homeFaq: {title: publicCopy.faqTitle, items: publicCopy.questions.map((q,i) => ({q, a: [facts.pricing, facts.privacy, facts.browser, t.uploadHint, facts.account][i]}))},
  };

  const tools = useMemo(() => [
    { id: 'karaoke-maker', icon: IconMicHandheld, name: lang === 'ja' ? 'カラオケメーカー' : 'Karaoke Maker', desc: lang === 'ja' ? '曲からボーカルを抜いて、インスト音源を作成。' : 'Remove vocals and create an instrumental track.', action: 'stemSeparator' as const, available: true },
    { id: 'vocal-remover', icon: IconMicHandheld, name: t.vocalName, desc: t.vocalDesc, action: 'stemSeparator' as const, available: true },
    { id: 'stem-separator', icon: IconLayers, name: t.stemName, desc: t.stemDesc, action: 'stemSeparator' as const, available: true },
    { id: 'mastering', icon: IconSpeaker, name: t.masterName, desc: t.masterDesc, action: () => navigate('/editor?tool=master'), available: true },
    { id: 'noise-removal', icon: IconSpeakerMute, name: t.noiseName, desc: t.noiseDesc, action: 'noiseRemoval' as const, available: true },
    { id: 'editor', icon: IconSliders, name: t.dawName, desc: t.dawDesc, action: 'editor' as const, available: true },
    { id: 'bpm-detect', icon: IconActivity, name: t.bpmName, desc: t.bpmDesc, action: 'bpmKeyDetector' as const, available: true },
    { id: 'autotune', icon: IconWave, name: t.autotuneName, desc: t.autotuneDesc, action: 'autotune' as const, available: true },
    { id: 'audio-to-midi', icon: IconMusicNote, name: t.audioMidiName, desc: t.audioMidiDesc, action: 'audioToMidi' as const, available: true },
    { id: 'nam', icon: IconAmp, name: t.namName, desc: t.namDesc, action: null, available: true },
    { id: 'sheet-music-ocr', icon: IconMusicNote, name: t.sheetOcrName, desc: t.sheetOcrDesc, action: 'sheetMusicOCR' as const, available: true },
    { id: 'music-generator', icon: IconSparkles, name: t.musicGenName, desc: t.musicGenDesc, action: 'musicGen' as const, available: true },
    { id: 'loop-forge', icon: IconRepeat, name: t.loopForgeName, desc: t.loopForgeDesc, action: null, available: true },
  ], [lang, t]);

  const pendingToolRef = useRef<string | null>(null);

  const handleFileUpload = useCallback(async (file: File, inputSource: 'demo' | 'upload' = 'upload') => {
    // Read current path to determine tool if pendingToolRef is not set.
    // stripLangPrefix only strips NON-default lang prefixes, but the DEFAULT
    // lang's prefixed URLs (/ja/autotune) are also served publicly — without
    // stripping /ja too, the lookup missed and fell back to stem-separator
    // (E2E 2026-07-06: uploading on /ja/autotune opened the Stem Separator).
    const currentPath = stripLangPrefix(window.location.pathname).replace(/^\/ja(?=\/|$)/, '');
    const pathToolMap: Record<string, string> = {
      '/karaoke-maker': 'karaoke-maker',
      '/vocal-remover': 'vocal-remover',
      '/stem-separation': 'stem-separator',
      '/noise-removal': 'noise-removal',
      '/bpm-detector': 'bpm-detect',
      '/amp-simulator': 'nam',
      '/autotune': 'autotune',
      '/audio-to-midi': 'audio-to-midi',
      '/daw-editor': 'editor',
      '/sheet-music-ocr': 'sheet-music-ocr',
      '/music-generator': 'music-generator',
    };
    const toolId = pendingToolRef.current || pathToolMap[currentPath] || 'stem-separator';
    pendingToolRef.current = null;
    // Mobile/cloud file pickers frequently report an empty MIME type or a
    // generic 'application/octet-stream' for perfectly valid audio files
    // (esp. FLAC/WAV from iOS Files / Google Drive). Fall back to a file
    // extension allowlist before rejecting — only reject if BOTH checks fail.
    const hasAllowedExtension = ACCEPTED_AUDIO_EXTENSIONS.some(ext =>
      file.name.toLowerCase().endsWith(`.${ext}`));
    if (!file.type.startsWith('audio/') && !hasAllowedExtension) {
      trackProcessFailed(toolId, 'file_select', 'unsupported_file_type', { file_type: file.type || 'unknown', lang });
      window.alert(lang === 'ja'
        ? '音声ファイル（MP3 / WAV / FLAC / M4A / OGG など）を選択してください。'
        : 'Please select an audio file (MP3, WAV, FLAC, M4A, OGG, etc.).');
      return;
    }
    trackFileUpload(toolId, file, { lang, source: inputSource });
    trackToolStart(toolId, { trigger: 'landing_upload', lang, source: inputSource });
    try {
      if (toolId === 'editor') {
        await setFile(file, 'editorImport', false, 'editor');
        navigate('/editor');
      } else if (toolId === 'autotune') {
        await setFile(file, 'autotune', false, toolId, null, { inputSource });
        navigate(isElectronApp() ? '/desktop/autotune' : '/editor?tool=autotune');
      } else if (toolId === 'noise-removal') {
        await setFile(file, 'noiseRemoval', false, toolId);
        navigate('/editor?tool=noiseRemoval');
      } else if (toolId === 'bpm-detect') {
        await setFile(file, 'bpmKeyDetector', false, toolId);
        navigate('/editor?tool=bpmKeyDetector');
      } else if (toolId === 'audio-to-midi') {
        await setFile(file, 'audioToMidi', false, toolId);
        navigate('/editor?tool=audioToMidi');
      } else if (toolId === 'nam') {
        // Crossing the public/editor Next layouts reloads the document.
        // Persist the input until the editor's NAM track can receive it.
        await setFile(file, 'namImport', false, 'nam');
        navigate('/editor?demo=nam');
      } else {
        const muteVocals = toolId === 'vocal-remover' || toolId === 'karaoke-maker';
        await setFile(file, 'stemSeparator', muteVocals, toolId);
        navigate('/editor?tool=stemSeparator');
      }
    } catch (e) {
      // setFile persists in-memory first and never throws for IDB failures,
      // so this only fires on truly unexpected errors (e.g. unreadable file).
      console.warn('[HomePage] file upload handoff failed', e);
      trackProcessFailed(toolId, 'upload', e, { lang });
      window.alert(lang === 'ja'
        ? 'ファイルの読み込みに失敗しました。もう一度お試しください。'
        : 'Failed to load the file. Please try again.');
    }
  }, [lang, setFile, navigate]);

  // One-click demo for the autotune landing: ~35% of tool_start users never
  // upload a file — give them an instant way to reach the editor experience.
  const [demoLoading, setDemoLoading] = React.useState(false);
  const handleDemoAudio = useCallback(async () => {
    if (demoLoading) return;
    setDemoLoading(true);
    trackEvent('autotune_demo_clicked', { lang });
    try {
      const res = await fetch('/demo/autotune-demo.wav');
      if (!res.ok) throw new Error(`demo fetch ${res.status}`);
      const blob = await res.blob();
      const file = new File([blob], 'sample-voice.wav', { type: 'audio/wav' });
      pendingToolRef.current = 'autotune';
      await handleFileUpload(file, 'demo');
    } catch (e) {
      console.warn('[HomePage] demo audio load failed', e);
      trackProcessFailed('autotune', 'demo_fetch', e, { lang });
      window.alert(lang === 'ja'
        ? 'デモ音声の読み込みに失敗しました。時間をおいてもう一度お試しください。'
        : 'Failed to load the demo audio. Please try again in a moment.');
      setDemoLoading(false);
    }
  }, [demoLoading, lang, handleFileUpload]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  }, [handleFileUpload]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  // Derive selected tool from URL
  const toolIdFromPath: Record<string, string> = {
    '/karaoke-maker': 'karaoke-maker',
    '/vocal-remover': 'vocal-remover',
    '/stem-separation': 'stem-separator',
    '/mastering': 'mastering',
    '/noise-removal': 'noise-removal',
    '/daw-editor': 'editor',
    '/bpm-detector': 'bpm-detect',
    '/amp-simulator': 'nam',
    '/autotune': 'autotune',
    '/audio-to-midi': 'audio-to-midi',
    '/sheet-music-ocr': 'sheet-music-ocr',
    '/music-generator': 'music-generator',
  };
  const toolPathMap: Record<string, string> = {
    'karaoke-maker': '/karaoke-maker',
    'vocal-remover': '/vocal-remover',
    'stem-separator': '/stem-separation',
    'mastering': '/mastering',
    'noise-removal': '/noise-removal',
    'editor': '/daw-editor',
    'bpm-detect': '/bpm-detector',
    'autotune': '/autotune',
    'audio-to-midi': '/audio-to-midi',
    'nam': '/editor?demo=nam',
    'sheet-music-ocr': '/sheet-music-ocr',
    'music-generator': '/music-generator',
    'loop-forge': '/loop-forge',
  };
  const editorToolMap: Record<string, string> = {
    'karaoke-maker': '/editor?tool=stemSeparator',
    'vocal-remover': '/editor?tool=stemSeparator',
    'stem-separator': '/editor?tool=stemSeparator',
    'noise-removal': '/editor?tool=noiseRemoval',
    'editor': '/editor',
    'bpm-detect': '/editor?tool=bpmKeyDetector',
    'autotune': '/desktop/autotune',
    'audio-to-midi': '/editor?tool=audioToMidi',
    'nam': '/editor?demo=nam',
    'sheet-music-ocr': '/sheet-music-ocr',
    'music-generator': '/editor?tool=musicGen',
    'loop-forge': '/editor?tool=loopforge',
  };
  const getToolHref = useCallback((toolId: string): string => {
    if (isElectronApp()) return editorToolMap[toolId] || '/editor';
    const path = toolPathMap[toolId] || '/';
    // Loop Forge is a standalone Next page with no locale-prefixed routes
    // (unlike the /[lang]/<tool> pages), so its href must bypass localizedPath.
    return path.startsWith('/editor') || path === '/loop-forge' ? path : localizedPath(path);
  }, [localizedPath]);
  const barePath = toolPath || (typeof window !== 'undefined' ? stripLangPrefix(window.location.pathname) : '/');
  const selectedToolId = toolIdFromPath[barePath] || null;
  const isHome = !selectedToolId;

  // Redirect signed-in users to /home when they hit the root landing.
  // Tool sub-pages (vocal-remover, stem-separation, etc.) stay accessible
  // so signed-in users can still deep-link to specific tools.
  const authedUser = useAuthStore((s) => s.user);
  const authIsLoading = useAuthStore((s) => s.isLoading);
  useEffect(() => {
    if (isHome && !authIsLoading && authedUser) {
      router.replace(localizedPath('/home'));
    }
  }, [isHome, authIsLoading, authedUser, router, localizedPath]);
  const selectedTool = tools.find((tt) => tt.id === selectedToolId) || tools[0];

  const toolUploadCopy = useMemo(() => {
    const copy: Record<string, { text: string; hint: string; outcomes: string[] }> = {
      'karaoke-maker': {
        text: lang === 'ja' ? '曲をアップロードしてカラオケ音源を作る' : 'Upload a song to make a karaoke track',
        hint: lang === 'ja' ? 'ボーカル除去後、インストWAVの試聴・ダウンロード・録音準備まで進めます' : 'Preview and download the instrumental WAV, then prepare a vocal recording track',
        outcomes: lang === 'ja'
          ? ['インストWAVを書き出し', 'ボーカル録音トラックを自動作成', '6ステムをエディタに展開']
          : ['Export an instrumental WAV', 'Create a vocal recording track', 'Open all 6 stems in the editor'],
      },
      'vocal-remover': {
        text: lang === 'ja' ? '音源をアップロードしてボーカルを除去' : 'Upload audio to remove vocals',
        hint: lang === 'ja' ? 'インスト、ボーカル、各ステムをブラウザ内で生成します' : 'Generate instrumental, vocals, and stems inside your browser',
        outcomes: lang === 'ja'
          ? ['インストをダウンロード', 'アカペラを抽出', 'リミックス用ステムを作成']
          : ['Download the instrumental', 'Extract acapella', 'Create stems for remixing'],
      },
      'stem-separator': {
        text: lang === 'ja' ? '曲をアップロードして6ステムに分離' : 'Upload a song to split 6 stems',
        hint: lang === 'ja' ? 'ドラム、ベース、ボーカル、ギター、ピアノ、その他に分離します' : 'Separate drums, bass, vocals, guitar, piano, and other',
        outcomes: lang === 'ja'
          ? ['各ステムを試聴', 'WAVで個別保存', 'ミックス編集へ移動']
          : ['Preview each stem', 'Save each stem as WAV', 'Continue into mix editing'],
      },
      autotune: {
        text: lang === 'ja' ? '歌声を選んで音程を補正' : 'Choose a vocal recording to tune',
        hint: lang === 'ja' ? 'MP3・WAV・FLAC・M4A — まず短い歌声で試してください' : 'MP3, WAV, FLAC, M4A — start with a short solo vocal',
        outcomes: lang === 'ja'
          ? ['キーとノートを確認', '音程を直して試聴', 'ApplyしてWAV保存']
          : ['Check the key and notes', 'Correct pitch and preview', 'Apply and download WAV'],
      },
      'audio-to-midi': {
        text: lang === 'ja' ? '音声をアップロードしてMIDIに変換' : 'Upload audio to convert it to MIDI',
        hint: lang === 'ja' ? '変換後、MIDI保存とピアノロール編集にそのまま進めます' : 'Download MIDI or open it directly in the piano roll after conversion',
        outcomes: lang === 'ja'
          ? ['MIDIを書き出し', 'ピアノロールで修正', 'メロディやコードを採譜']
          : ['Export MIDI', 'Edit in the piano roll', 'Transcribe melodies and chords'],
      },
      'noise-removal': {
        text: lang === 'ja' ? '音声をアップロードしてノイズを除去' : 'Upload audio to remove noise',
        hint: lang === 'ja' ? '録音、ボイスメモ、ポッドキャスト素材をブラウザ内でクリーンアップします' : 'Clean recordings, voice memos, and podcast audio in your browser',
        outcomes: lang === 'ja'
          ? ['処理後の音を試聴', 'クリーンなWAVを書き出し', 'エディタで続けて編集']
          : ['Preview the cleaned audio', 'Export a clean WAV', 'Keep editing in the editor'],
      },
      nam: {
        text: lang === 'ja' ? 'ギターのDI録音をアップロードしてアンプを鳴らす' : 'Upload a DI guitar recording to run it through an amp',
        hint: lang === 'ja' ? 'NAMアンプとキャビネットIRがロード済みのエディタが開きます' : 'The editor opens with a NAM amp and cabinet IR already loaded',
        outcomes: lang === 'ja'
          ? ['NAMアンプで音作り', 'キャビネットIRを切り替え', 'そのままミックス・書き出し']
          : ['Dial in a NAM amp tone', 'Swap cabinet IRs', 'Mix and export right away'],
      },
    };
    if (localeControls) return selectedToolId === 'autotune' ? {text: localeControls.upload, hint: localeControls.hint, outcomes: localeControls.outcomes} : undefined;
    return selectedToolId ? copy[selectedToolId] : undefined;
  }, [lang, selectedToolId]);

  useEffect(() => {
    if (typeof window === 'undefined' || !selectedToolId) return;
    trackEvent('tool_landing_view', {
      tool_name: selectedToolId,
      lang,
      page_path: window.location.pathname,
    });
    const params = new URLSearchParams(window.location.search);
    const articleSlug = params.get('ref_article');
    if (!articleSlug) return;
    trackArticleToolTransition({
      articleSlug,
      targetTool: selectedToolId,
      ctaPosition: params.get('cta') || undefined,
    });
  }, [selectedToolId, lang]);

  const handleUploadClick = () => {
    if (selectedTool.action === 'musicGen') {
      trackToolStart('music-generator', { trigger: 'landing_cta', lang });
      navigate('/editor?tool=musicGen');
      return;
    }
    trackToolStart(selectedToolId || selectedTool.id, { trigger: 'landing_cta', lang });
    pendingToolRef.current = selectedToolId;
    fileInputRef.current?.click();
  };

  if (isHome) {
    // ===== HOME: Apple-style landing, the DAW is the hero (HomeLanding.tsx) =====
    return (
      <HomeLanding
        lang={lang}
        setLang={setLang}
        localizedPath={localizedPath}
        getToolHref={getToolHref}
        tools={tools}
        t={t}
        facts={facts}
        home={home}
      />
    );
  }

  // ===== TOOL PAGE: Sidebar + upload area =====
  return (
    <div className="landing landing--sidebar">
      <Link className="landing-logo-fixed" href={localizedPath('/')} aria-label="LA Studio home">
        <svg width="140" height="28" viewBox="0 0 140 28" fill="none">
          <text x="0" y="22" fontSize="24" fontFamily="'Montserrat', sans-serif" fontWeight="700" letterSpacing="1" fill="#1a1610">L</text>
          <text x="16" y="22" fontSize="24" fontFamily="'Montserrat', sans-serif" fontWeight="700" letterSpacing="1" fill="#d4a853">A</text>
          <rect x="38" y="7" width="2" height="14" rx="1" fill="#d4a853"/>
          <text x="46" y="20" fontSize="14" fontFamily="'Montserrat', sans-serif" fontWeight="600" letterSpacing="3" fill="#8b7355">STUDIO</text>
        </svg>
      </Link>

      <aside className="landing-sidebar">
        <nav className="landing-sidebar-nav">
          <Link className="landing-sidebar-btn" href={localizedPath('/')} title="Home">
            <span className="landing-sidebar-icon">{IconHome}</span>
            <span className="landing-sidebar-label">Home</span>
          </Link>
          {tools.map((tool) => (
            <Link
              key={tool.id}
              className={`landing-sidebar-btn ${selectedToolId === tool.id ? 'landing-sidebar-btn--active' : ''} ${!tool.available ? 'landing-sidebar-btn--disabled' : ''}`}
              href={tool.available ? getToolHref(tool.id) : '#'}
              title={tool.name}
              aria-disabled={!tool.available}
              onClick={(e) => {
                if (!tool.available) e.preventDefault();
                if (tool.available) {
                  trackToolStart(tool.id, {
                    trigger: 'tool_sidebar_nav',
                    lang,
                    from_tool: selectedToolId || 'home',
                  });
                }
              }}
            >
              <span className="landing-sidebar-icon">{tool.icon}</span>
              <span className="landing-sidebar-label">{tool.name}</span>
              {!tool.available && <span className="landing-sidebar-soon">Soon</span>}
            </Link>
          ))}
        </nav>
        <div className="landing-sidebar-bottom">
          <select className="landing-sidebar-lang" value={lang} onChange={(e) => setLang(e.target.value as Lang)}>
            {LANGS.map((l) => (<option key={l} value={l}>{LANG_LABELS[l]}</option>))}
          </select>
        </div>
      </aside>

      <main className="landing-main">
        <div className="landing-main-content">
          <p className="landing-tool-conditions">{selectedToolId === 'autotune'
            ? (localeControls ? localeControls.quota : lang === 'ja'
              ? `ノート編集・試聴は無料。適用は未登録で${FREE_APPLY_USES_PER_DAY_ANON}回/日、無料登録で${FREE_APPLY_USES_PER_DAY_REGISTERED}回/日。`
              : `Free note editing and auditioning. ${FREE_APPLY_USES_PER_DAY_ANON} full apply/day without signup; ${FREE_APPLY_USES_PER_DAY_REGISTERED}/day with a free account.`)
            : facts.account}</p>
          {/*
            SEO h1 override for the stem page (JA/EN only): claims the
            verified zero-competition angle "free 6-stem splitter with guitar
            + piano, no signup". Sidebar labels keep the short tool name.
          */}
          <h1 className="landing-tool-title">
            {(selectedToolId && (['ja', 'en'].includes(lang) ? H1_OVERRIDES[selectedToolId]?.[lang as 'ja' | 'en'] : undefined)) || selectedTool.name}
          </h1>
          <p className="landing-tool-description">{selectedTool.desc}</p>
          {selectedToolId === 'karaoke-maker' && (
            <div className="landing-outcome-steps">
              <div><strong>1</strong><span>{lang === 'ja' ? '曲を選ぶ' : publicCopy?.steps[0] || 'Choose a song'}</span></div>
              <div><strong>2</strong><span>{lang === 'ja' ? 'ボーカルを除去' : publicCopy?.steps[1] || 'Remove vocals'}</span></div>
              <div><strong>3</strong><span>{lang === 'ja' ? 'インストを書き出し' : publicCopy?.steps[2] || 'Export the instrumental'}</span></div>
            </div>
          )}
          {selectedToolId === 'audio-to-midi' && (
            <div className="landing-outcome-steps">
              <div><strong>1</strong><span>{lang === 'ja' ? '音源を選ぶ' : publicCopy?.steps[0] || 'Choose audio'}</span></div>
              <div><strong>2</strong><span>{lang === 'ja' ? 'AIで採譜' : publicCopy?.steps[1] || 'Transcribe notes'}</span></div>
              <div><strong>3</strong><span>{lang === 'ja' ? 'MIDIを書き出し' : publicCopy?.steps[2] || 'Export MIDI'}</span></div>
            </div>
          )}
          {selectedToolId === 'bpm-detect' && (
            <div className="landing-outcome-steps">
              <div><strong>1</strong><span>{lang === 'ja' ? '曲を解析' : publicCopy?.steps[0] || 'Analyze a track'}</span></div>
              <div><strong>2</strong><span>{lang === 'ja' ? 'BPMとキー確認' : publicCopy?.steps[1] || 'Find BPM and key'}</span></div>
              <div><strong>3</strong><span>{lang === 'ja' ? 'DJ準備に使う' : publicCopy?.steps[2] || 'Prepare DJ sets'}</span></div>
            </div>
          )}
          {selectedToolId === 'sheet-music-ocr' ? (
            <React.Suspense fallback={<p role="status">{preparingInput}</p>}><SheetMusicOCRPanel /></React.Suspense>
          ) : selectedToolId === 'music-generator' ? (
            <div
              className="landing-upload"
              onClick={() => navigate('/editor?tool=musicGen')}
              style={{ cursor: 'pointer' }}
            >
              <div className="landing-upload-icon">{IconSparkles}</div>
              <p className="landing-upload-text">
                {lang === 'ja' ? 'エディタで Music Generator を開く' : 'Open Music Generator in the editor'}
              </p>
              <p className="landing-upload-hint">
                {lang === 'ja'
                  ? 'ログイン + クレジット (¥5〜/曲) で使用できます'
                  : 'Login + credits required (¥5+/song)'}
              </p>
            </div>
          ) : selectedToolId === 'mastering' ? (
            // AI Mastering ships inside the editor's DEVICES rack (AI MASTER
            // + reference matching), not as a standalone pipeline. This page
            // used to show a "coming soon" dead end — 21 tool_unavailable_click
            // in one day after the /mastering LP launched — so send the demand
            // to the feature that exists.
            <div
              className="landing-upload"
              onClick={() => {
                trackEvent('tool_to_editor', {
                  tool_name: 'mastering',
                  page_path: typeof window !== 'undefined' ? window.location.pathname : '',
                  lang,
                });
                navigate('/editor?tool=master');
              }}
              style={{ cursor: 'pointer' }}
            >
              <div className="landing-upload-icon">{selectedTool.icon}</div>
              <p className="landing-upload-text">{t.masterSoonText}</p>
              <p className="landing-upload-hint">{t.masterSoonHint}</p>
            </div>
          ) : !selectedTool.available ? (
            // This tool (e.g. 'mastering') has no real processing pipeline yet.
            // Previously this fell through to the generic dropzone below, which
            // silently rerouted uploads into the Stem Separator while tagging
            // GA events with tool_name=<this tool> — file_upload fired but the
            // user never got what they asked for, so process_complete never
            // followed. Show an explicit "coming soon" state instead so nobody
            // burns an upload on a feature that doesn't exist.
            <div
              className="landing-upload landing-upload--disabled"
              // Dedicated event, NOT process_failed — this is demand signal for an
              // unbuilt tool, and must not pollute the funnel-health metrics.
              onClick={() => trackEvent('tool_unavailable_click', {
                tool_name: selectedToolId || selectedTool.id,
                page_path: typeof window !== 'undefined' ? window.location.pathname : '',
                lang,
              })}
            >
              <div className="landing-upload-icon">{selectedTool.icon}</div>
              <p className="landing-upload-text">{t.masterSoonText}</p>
              <p className="landing-upload-hint">{t.masterSoonHint}</p>
            </div>
          ) : (
          <>
          <button
            type="button"
            className={`landing-upload ${dragOver ? 'landing-upload--active' : ''}`}
            disabled={!inputReady}
            aria-busy={!inputReady}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={handleUploadClick}
          >
            <span className="landing-upload-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            </span>
            <span className="landing-upload-text">{toolUploadCopy?.text || t.uploadText}</span>
            <span className="landing-upload-hint">{inputReady ? (toolUploadCopy?.hint || t.uploadHint) : preparingInput}</span>
          </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={AUDIO_FILE_ACCEPT}
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(file);
                e.target.value = '';
              }}
            />
          </>
          )}
          {selectedToolId === 'autotune' && (
            <button
              type="button"
              className="landing-demo-btn"
              disabled={!inputReady || demoLoading}
              aria-busy={!inputReady || demoLoading}
              onClick={handleDemoAudio}
            >
              {!inputReady ? preparingInput : demoLoading
                ? (localeControls ? localeControls.loading : lang === 'ja' ? 'サンプルを読み込み中…' : lang === 'zh' ? '示例加载中…' : 'Loading sample…')
                : (localeControls ? localeControls.demo : lang === 'ja' ? '🎤 サンプル音声で試す（アップロード不要）' : lang === 'zh' ? '🎤 用示例音频试一试（无需上传）' : '🎤 Try with a sample voice — no upload needed')}
            </button>
          )}
          {selectedToolId && selectedToolId !== 'autotune' && toolUploadCopy && (
            <div className="landing-tool-outcomes" aria-label="What happens next">
              {toolUploadCopy.outcomes.map((outcome) => (
                <div key={outcome} className="landing-tool-outcome">
                  <span />
                  <strong>{outcome}</strong>
                </div>
              ))}
            </div>
          )}

        </div>
        <footer className="landing-footer">
          <p>{t.footer}</p>
          <div className="landing-footer-links">
            <Link href={localizedPath('/about')}>About</Link>
            <Link href={localizedPath('/pricing')}>Pricing</Link>
            <Link href={localizedPath('/privacy')}>Privacy Policy</Link>
            <Link href={localizedPath('/terms')}>Terms of Service</Link>
            <Link href={localizedPath('/tokushoho')}>特定商取引法</Link>
            <Link href={localizedPath('/contact')}>Contact</Link>
            <Link href={localizedPath('/blog')}>Blog</Link>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default HomePage;
